import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Sparkles, AlertCircle } from 'lucide-react';
import { ScatterPlot, CorrelationHeatmap } from '../charts';

export default function CorrelationInsightsPanel({ data, columns, onAnalyze }) {
  const [correlationData, setCorrelationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [showMatrix, setShowMatrix] = useState(false);

  // Auto-detect numeric columns
  useEffect(() => {
    if (data && data.length > 0) {
      const numericCols = columns.filter(col => {
        const value = data[0][col];
        return typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value));
      });
      
      if (numericCols.length >= 2) {
        setSelectedAttributes([numericCols[0], numericCols[1]]);
      }
    }
  }, [data, columns]);

  // Calculate correlation when attributes are selected
  const calculateCorrelation = async () => {
    if (selectedAttributes.length < 2) return;

    setLoading(true);
    try {
      const response = await fetch('/api/correlation-engine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'calculate-correlation',
          attribute1: selectedAttributes[0],
          attribute2: selectedAttributes[1],
          filters: {}
        })
      });

      const result = await response.json();
      if (result.success) {
        setCorrelationData(result);
      }
    } catch (error) {
      console.error('Correlation calculation error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get correlation matrix for all numeric columns
  const getCorrelationMatrix = async () => {
    const numericCols = columns.filter(col => {
      const value = data[0][col];
      return typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value));
    });

    if (numericCols.length < 2) return;

    setLoading(true);
    try {
      const response = await fetch('/api/correlation-engine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'correlation-matrix',
          attributes: numericCols.slice(0, 8), // Limit to 8 attributes for readability
          filters: {}
        })
      });

      const result = await response.json();
      if (result.success) {
        setCorrelationData(result);
        setShowMatrix(true);
      }
    } catch (error) {
      console.error('Correlation matrix error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCorrelationIcon = (coefficient) => {
    if (coefficient > 0.2) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (coefficient < -0.2) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'strong': return 'text-green-400';
      case 'moderate': return 'text-yellow-400';
      case 'weak': return 'text-orange-400';
      default: return 'text-slate-400';
    }
  };

  if (!data || data.length === 0) {
    return null;
  }

  const numericColumns = columns.filter(col => {
    const value = data[0][col];
    return typeof value === 'number' || (!isNaN(parseFloat(value)) && isFinite(value));
  });

  if (numericColumns.length < 2) {
    return (
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <div className="flex items-center gap-2 text-slate-400">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">Need at least 2 numeric columns for correlation analysis</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-slate-100">Correlation Insights</h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={calculateCorrelation}
            disabled={loading || selectedAttributes.length < 2}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm rounded-lg transition-colors"
          >
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
          <button
            onClick={getCorrelationMatrix}
            disabled={loading}
            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
          >
            Full Matrix
          </button>
        </div>
      </div>

      {/* Attribute Selection */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Attribute 1</label>
          <select
            value={selectedAttributes[0] || ''}
            onChange={(e) => setSelectedAttributes([e.target.value, selectedAttributes[1]])}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
          >
            {numericColumns.map(col => (
              <option key={col} value={col}>{col.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Attribute 2</label>
          <select
            value={selectedAttributes[1] || ''}
            onChange={(e) => setSelectedAttributes([selectedAttributes[0], e.target.value])}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
          >
            {numericColumns.map(col => (
              <option key={col} value={col}>{col.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Results */}
      {correlationData && !showMatrix && correlationData.correlation && (
        <div className="space-y-4">
          {/* Correlation Summary */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-slate-400 mb-1">Correlation</div>
                <div className="flex items-center gap-2">
                  {getCorrelationIcon(correlationData.correlation.coefficient)}
                  <span className="text-2xl font-bold text-slate-100">
                    {correlationData.correlation.coefficient.toFixed(3)}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Strength</div>
                <div className={'text-lg font-semibold ' + getStrengthColor(correlationData.correlation.strength)}>
                  {correlationData.correlation.strength.toUpperCase()}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 mb-1">Sample Size</div>
                <div className="text-lg font-semibold text-slate-100">
                  n = {correlationData.correlation.sampleSize}
                </div>
              </div>
            </div>

            {/* Interpretation */}
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-300">
                {getCorrelationInterpretation(correlationData.correlation)}
              </p>
            </div>
          </div>

          {/* Scatter Plot */}
          {correlationData.scatterData && (
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="h-80">
                <ScatterPlot
                  data={correlationData.scatterData}
                  xKey="x"
                  yKey="y"
                  xLabel={selectedAttributes[0]?.replace(/_/g, ' ')}
                  yLabel={selectedAttributes[1]?.replace(/_/g, ' ')}
                  correlation={correlationData.correlation}
                  showTrendLine={true}
                />
              </div>
            </div>
          )}

          {/* Statistics */}
          {correlationData.statistics && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <h4 className="text-sm font-semibold text-slate-300 mb-3">
                  {selectedAttributes[0]?.replace(/_/g, ' ')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Mean:</span>
                    <span className="text-slate-100">{correlationData.statistics.attr1.mean.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Std Dev:</span>
                    <span className="text-slate-100">{correlationData.statistics.attr1.stdDev.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Range:</span>
                    <span className="text-slate-100">
                      {correlationData.statistics.attr1.min.toFixed(1)} - {correlationData.statistics.attr1.max.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <h4 className="text-sm font-semibold text-slate-300 mb-3">
                  {selectedAttributes[1]?.replace(/_/g, ' ')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Mean:</span>
                    <span className="text-slate-100">{correlationData.statistics.attr2.mean.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Std Dev:</span>
                    <span className="text-slate-100">{correlationData.statistics.attr2.stdDev.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Range:</span>
                    <span className="text-slate-100">
                      {correlationData.statistics.attr2.min.toFixed(1)} - {correlationData.statistics.attr2.max.toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Correlation Matrix */}
      {correlationData && showMatrix && correlationData.matrix && (
        <div className="space-y-4">
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <CorrelationHeatmap
              matrix={correlationData.matrix}
              attributes={correlationData.attributes}
              title="Correlation Matrix"
            />
          </div>

          {/* Top Correlations */}
          {correlationData.topCorrelations && (
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Top Correlations</h4>
              <div className="space-y-2">
                {correlationData.topCorrelations.slice(0, 5).map((corr, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                    <div className="flex items-center gap-2">
                      {getCorrelationIcon(corr.coefficient)}
                      <span className="text-sm text-slate-300">
                        {corr.attribute1.replace(/_/g, ' ')} × {corr.attribute2.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={'text-sm font-semibold ' + getStrengthColor(corr.strength)}>
                        {corr.strength}
                      </span>
                      <span className="text-sm font-mono text-slate-100">
                        {corr.coefficient.toFixed(3)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => setShowMatrix(false)}
            className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
          >
            Back to Pair Analysis
          </button>
        </div>
      )}
    </div>
  );
}

// Helper function to generate correlation interpretation
function getCorrelationInterpretation(correlation) {
  const { coefficient, strength, attribute1, attribute2 } = correlation;
  // const abs = Math.abs(coefficient);
  const direction = coefficient > 0 ? 'positive' : 'negative';
  
  const attr1 = attribute1.replace(/_/g, ' ');
  const attr2 = attribute2.replace(/_/g, ' ');

  if (strength === 'strong') {
    return 'Strong ' + direction + ' correlation detected. As ' + attr1 + ' ' + (coefficient > 0 ? 'increases' : 'decreases') + ', ' + attr2 + ' tends to ' + (coefficient > 0 ? 'increase' : 'decrease') + ' significantly. This relationship is highly predictive.';
  } else if (strength === 'moderate') {
    return 'Moderate ' + direction + ' correlation found. There is a noticeable relationship between ' + attr1 + ' and ' + attr2 + ', though other factors also play a role.';
  } else if (strength === 'weak') {
    return 'Weak ' + direction + ' correlation observed. ' + attr1 + ' and ' + attr2 + ' show some relationship, but it\'s not very strong. Consider other factors.';
  } else {
    return 'No significant correlation detected between ' + attr1 + ' and ' + attr2 + '. These attributes appear to be independent.';
  }
}
